import { QueryClient } from '@tanstack/react-query';
import { safeLocalStorage } from '../utils/storage';

const REACT_QUERY_CACHE_KEY = 'react-query-cache';
const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

function hasLoggedInSession(): boolean {
  try {
    const token =
      sessionStorage.getItem(AUTH_TOKEN_KEY) ?? safeLocalStorage.getItem(AUTH_TOKEN_KEY);
    const user =
      sessionStorage.getItem(AUTH_USER_KEY) ?? safeLocalStorage.getItem(AUTH_USER_KEY);
    return Boolean(token && user);
  } catch {
    return false;
  }
}

function loadPersistedCache(): Record<string, unknown> | null {
  if (!hasLoggedInSession()) return null;
  try {
    const cached = sessionStorage.getItem(REACT_QUERY_CACHE_KEY);
    if (!cached) return null;
    return JSON.parse(cached) as Record<string, unknown>;
  } catch (error) {
    console.warn('Failed to load React Query cache:', error);
    return null;
  }
}

function savePersistedCache(cacheData: Record<string, unknown>) {
  if (!hasLoggedInSession()) {
    clearPersistedQueryCache();
    return;
  }
  try {
    sessionStorage.setItem(REACT_QUERY_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to save React Query cache:', error);
    try {
      sessionStorage.removeItem(REACT_QUERY_CACHE_KEY);
      sessionStorage.setItem(REACT_QUERY_CACHE_KEY, JSON.stringify(cacheData));
    } catch (e) {
      console.error('Failed to save cache even after clearing:', e);
    }
  }
}

export function clearPersistedQueryCache() {
  try {
    sessionStorage.removeItem(REACT_QUERY_CACHE_KEY);
  } catch {
    // Ignore.
  }
}

/** Snapshot successful React Query entries to sessionStorage (logged-in only). */
export function persistQueryCacheNow() {
  if (!hasLoggedInSession()) {
    clearPersistedQueryCache();
    return;
  }

  const cacheData: Record<string, unknown> = {};
  queryClient.getQueryCache().getAll().forEach((query) => {
    if (query.state.data !== undefined && query.state.status === 'success') {
      cacheData[JSON.stringify(query.queryKey)] = query.state.data;
    }
  });

  if (Object.keys(cacheData).length > 0) {
    savePersistedCache(cacheData);
  } else {
    clearPersistedQueryCache();
  }
}

/** Drop in-memory + persisted React Query data (e.g. logout). */
export function clearQueryCacheForSession() {
  queryClient.clear();
  clearPersistedQueryCache();
}

// Restore only when a logged-in session already exists in storage.
const cachedData = loadPersistedCache();
if (cachedData) {
  try {
    Object.entries(cachedData).forEach(([key, data]) => {
      try {
        queryClient.setQueryData(JSON.parse(key), data);
      } catch {
        console.warn('Invalid query key in cache:', key);
      }
    });
  } catch (error) {
    console.warn('Failed to restore React Query cache:', error);
  }
}

let saveTimeout: ReturnType<typeof setTimeout> | null = null;
queryClient.getQueryCache().subscribe(() => {
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    persistQueryCacheNow();
  }, 500);
});

if (typeof window !== 'undefined') {
  const flushOnUnload = () => persistQueryCacheNow();
  window.addEventListener('beforeunload', flushOnUnload);
  window.addEventListener('pagehide', flushOnUnload);
}
